import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import usePreventNavigation from '../../../../hooks/usePreventNavigation';
import formatUSD from '../../../../utils/formatUSD';
import cartUtils from '../../../../utils/cart';
import {
  addToCart,
  clearRetouchingSelection,
  declineRetouching,
  finishRetouchingEdit,
  logRetouchingOffer,
  recordRetouchingOffered,
  resolveRetouchingOffer,
  updateRetouchingSelection,
} from '../../../../actions/v3Order';
import {
  selectRetouchingAddableForOrder,
  selectRetouchingPreCartOffers,
} from '../../../../selectors';
import { RETOUCHING_ORIGINALS_CAPTION } from '../../cart/RetouchingCartPhotos';
import ProductOptionFields from '../regular/subcomponents/ProductOptionFields';
import {
  getProductOptionSets,
  productOptionArgs,
  reconcileProductOptions,
  SelectedProductOptions,
} from '../regular/subcomponents/productOptionsHelpers';
import RetouchingLevelCard from './RetouchingLevelCard';
import RetouchingPhotoPicker from './RetouchingPhotoPicker';
import {
  exclusiveSelections,
  levelForPhoto,
  RetouchingSelections,
  setPhotoLevel,
  togglePhoto,
} from './retouchingSelections';
import { RetouchingPose, RetouchingProduct } from './types';

interface RetouchingCartItem {
  id: string;
  productId: number;
  isRetouching?: boolean;
  poseIds?: number[];
  selectedProductOptions?: SavedProductOption[];
  retouchingNotes?: Record<string, string>;
  retouchingSources?: Record<string, string>;
}

// Which UI added a photo to retouching; recorded per photo, first touch wins.
type RetouchingSource =
  | 'initial_prompt'
  | 'pre_cart_offer'
  | 'cart_chip'
  | 'catalog'
  | 'cart_edit';

// initial: the post-add screen; edit: a cart order's retouching (from its
// Edit link or a chip); precart: photos added since the order's last offer,
// shown once before the cart.
type RetouchingScreenMode = 'initial' | 'edit' | 'precart';

// One chosen option as a cart line stores it (cartUtils.productOptionCartFields).
interface SavedProductOption {
  optionSetKey?: string;
  optionKey?: string | null;
  optionId?: number | string;
  optionPrice?: number;
}

interface RetouchingOrder {
  event?: { id?: number | string };
  isPrepay?: boolean;
  cartItems?: RetouchingCartItem[];
  additionalData?: { retouchingRequiresImageSelection?: boolean };
  retouching?: { offeredPoseIds?: number[] };
  productContext?: {
    retouchingProducts?: RetouchingProduct[] | RetouchingProduct;
    retouchingRequiresImageSelection?: boolean;
  };
}

interface RetouchingState {
  additionalData: {
    selectedStudent: { id: number };
    retouchingRequiresImageSelection?: boolean;
  };
  shoot: {
    retouchingProducts?: RetouchingProduct[] | RetouchingProduct;
    shoot?: { id?: number; event_id?: number };
    isPrepay?: boolean;
  };
  v3Order: {
    orders?: Record<string, RetouchingOrder>;
    retouchingCheckoutProtected?: unknown;
    retouchingCheckoutRequest?: unknown;
    retouchingQuoteError?: unknown;
  };
}

interface RetouchingConfigProps {
  history: {
    push: (path: string) => void;
    replace?: (path: string) => void;
    goBack?: () => void;
    block: (...args: unknown[]) => () => void;
  };
}

// addToCart is untyped JS whose destructured arguments TypeScript infers as
// all required; this screen passes only the retouching subset.
const addRetouchingToCart = addToCart as unknown as (args: {
  studentId: number;
  fullproductData: RetouchingProduct;
  orderKeyOverride?: string | null;
  retouchingPricingMode?: 'per_image';
  poseIds?: number[];
  poses?: RetouchingPose[];
  quantity?: number;
  redirectSource?: 'cart' | 'stay' | null;
  retouchingNotes?: Record<number, string>;
  retouchingSources?: Record<number, RetouchingSource>;
  productOptions?: SelectedProductOptions;
  productOptionKey?: string | null;
}) => unknown;

const selectionSummary = (
  product: RetouchingProduct,
  count: number,
  selectedProductOptions: SavedProductOption[] = [],
): { text: string; totalCents: number } => {
  const plural = count === 1 ? '' : 's';

  const line = {
    price: product.price,
    poseIds: Array.from({ length: count }, (_, index) => index + 1),
    retouchingPricingMode: 'per_image',
    retouchingTieredUnitPricesCents: product.tiered_unit_prices_cents || [],
    selectedProductOptions,
  };
  const units: number[] = cartUtils.marginalRetouchingPrices({
    basePriceCents: line.price,
    scheduleCents: line.retouchingTieredUnitPricesCents,
    count,
  });
  const options = cartUtils.determineCartItemPrice(line) - line.price;
  const totalCents: number = cartUtils.cartLineTotal(line);
  const breakdown = `${units.map((cents) => formatUSD(cents)).join(' + ')}${
    options ? ` + ${formatUSD(options)} options per image` : ''
  }`;
  // One image at no option cost needs no "= total": it is its own price.
  const total =
    units.length === 1 && !options ? '' : ` = ${formatUSD(totalCents)}`;
  return {
    text: `${count} image${plural} selected — ${breakdown}${total}`,
    totalCents,
  };
};

// The shared header class pads all four sides; this screen's text has no
// side padding, so the title keeps only its vertical space and lines up
// with the text under it.
const Title = styled.h3`
  margin: 0;
  padding: 20px 0;
`;

const Intro = styled.p`
  max-width: 640px;
  margin: 0 0 6px;
  font-size: 16px;
  line-height: 1.5;
`;

const FlatIntro = styled(Intro)`
  margin-bottom: 20px;
`;

const Caption = styled.p`
  max-width: 640px;
  margin: 0 0 20px;
  font-size: 14px;
  line-height: 1.5;
  color: #5f6368;
`;

const Levels = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 28px;

  @media (max-width: 767px) {
    gap: 10px;
    margin-bottom: 20px;
  }
`;

const ACCENT = '#3085d6';

// Stays in view while the customer scrolls the photos, so the running total
// and the Add button are never a scroll away. The negative side margins let
// it span the page container's 15px gutters, so it reads as a pinned footer
// rather than a white slab the width of the content.
const ActionBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 24px;
  margin: 28px -15px 40px;
  padding: 14px 15px;
  background: #fff;
  border-top: 1px solid #e3e3e3;
  box-shadow: 0 -8px 20px rgba(0, 0, 0, 0.05);

  @media (max-width: 767px) {
    gap: 10px 16px;
    padding: 12px 15px;
  }
`;

const Summary = styled.div`
  flex: 1 1 280px;
  min-width: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #4a4a4a;
`;

// With several levels in play the per-level lines would take a quarter of a
// phone screen in the pinned bar, so phones get the count and total only;
// the cart lists each level.
const SummaryLine = styled.div<{ $collapseOnPhone: boolean }>`
  @media (max-width: 767px) {
    display: ${(props) => (props.$collapseOnPhone ? 'none' : 'block')};
  }
`;

const PhoneCount = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: block;
  }
`;

const SummaryTotal = styled.div`
  margin-top: 2px;
  font-size: 16px;
  font-weight: 600;
  color: #222;
`;

const NotesMissing = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #c0392b;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 767px) {
    flex: 1 1 100%;
    gap: 10px;
  }
`;

// The legacy classes carry the studio's button colors and are what the
// system specs target. Doubled specificity drops their 40px top margin and
// the native button chrome: `.bnl-product-config__button--cancel` never
// resets `border`, so without this the UA's outset border gives "No Thanks"
// a raised bevel next to the flat primary.
const ActionButton = styled.button`
  && {
    width: auto;
    min-width: 160px;
    max-width: none;
    min-height: 44px;
    margin: 0;
    padding: 10px 22px;
    border: 0;
    border-radius: 6px;
    box-shadow: none;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.2;
    appearance: none;
    -webkit-appearance: none;
  }

  /* Legacy class override: the quiet secondary. A solid dark-grey fill with
     white text reads as a second primary; a light neutral fill with dark text
     keeps the blue button the only strong color in the bar. */
  &&.bnl-product-config__button--cancel {
    background: #edf0f2;
    color: #30363d;
  }

  &&.bnl-product-config__button--cancel:hover {
    background: #e2e6ea;
  }

  &&:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }

  @media (max-width: 767px) {
    && {
      flex: 1 1 0;
      min-width: 0;
      padding: 10px 12px;
    }
  }
`;

const RetouchingConfig: FC<RetouchingConfigProps> = ({ history }) => {
  // The store is untyped, so its thunks go through an untyped dispatch.
  const dispatch = useDispatch() as unknown as (action: unknown) => void;
  const location = useLocation();

  const {
    selectedStudent,
    retouchingRequiresImageSelection: globallyEnabledImageSelection,
  } = useSelector((state: RetouchingState) => state.additionalData);
  const {
    retouchingProducts,
    shoot: shootInfo,
    isPrepay: shootIsPrepay,
  } = useSelector((state: RetouchingState) => state.shoot);
  const orders = useSelector((state: RetouchingState) => state.v3Order.orders);

  const queryParams = new URLSearchParams(location.search);
  const mode: RetouchingScreenMode =
    queryParams.get('offer') === 'precart'
      ? 'precart'
      : queryParams.get('edit') === '1'
      ? 'edit'
      : 'initial';
  const editMode = mode === 'edit';
  // Both edit and pre-cart work on a cart order named in the query: its
  // lines and notes are prefilled, and a level with a line is updated.
  const cartOrderMode = mode !== 'initial';
  // The level a tap selects (opened from the catalog tile or a chip).
  const requestedProductId = Number(queryParams.get('productId')) || null;
  // A chip's photo, honoured only in edit mode.
  const chipPoseId = editMode
    ? Number(queryParams.get('poseId')) || null
    : null;
  const source: RetouchingSource =
    mode === 'precart'
      ? 'pre_cart_offer'
      : chipPoseId
      ? 'cart_chip'
      : requestedProductId
      ? 'catalog'
      : editMode
      ? 'cart_edit'
      : 'initial_prompt';

  // The cart renders one section per student, so an Edit link names the order it
  // belongs to. Without it we would fall back to the globally selected student
  // and edit a different student's retouching.
  const requestedOrderKey = queryParams.get('orderKey');
  const shootEventId = shootInfo?.event_id || shootInfo?.id;
  const orderKey = cartOrderMode
    ? requestedOrderKey
    : `${shootEventId}-${selectedStudent.id}`;
  const currentOrder = orderKey ? orders?.[orderKey] : undefined;
  const productsForOrder =
    cartOrderMode && currentOrder?.productContext?.retouchingProducts
      ? currentOrder.productContext.retouchingProducts
      : cartOrderMode &&
        String(currentOrder?.event?.id ?? orderKey?.split('-')[0]) !==
          String(shootEventId)
      ? []
      : retouchingProducts || [];

  // Filter the list of retouching products by name to ensure we're not
  // displaying more than one instance.
  const filteredRetouchingProducts: RetouchingProduct[] = Array.isArray(
    productsForOrder,
  )
    ? productsForOrder.filter(
        (product, index) =>
          productsForOrder.findIndex(
            (candidate) => candidate.name === product.name,
          ) === index,
      )
    : [productsForOrder];
  const levels = filteredRetouchingProducts;
  const levelIds = levels.map((product) => product.id);
  // Tapping a photo puts it on this level; the level choices still offer
  // every level.
  const defaultLevel: RetouchingProduct | undefined =
    levels.find((product) => product.id === requestedProductId) ?? levels[0];

  const selectablePoses: RetouchingPose[] = useMemo(
    () => cartUtils.distinctCartPoses(currentOrder),
    [currentOrder],
  );

  // The photos added since the order's last offer, as the cart's pre-cart
  // gate saw them. Read once on arrival, because arriving records every shown
  // photo as offered and the live list then empties.
  const preCartOffers = useSelector(selectRetouchingPreCartOffers);
  const [newPoseIds] = useState<number[]>(() =>
    mode === 'precart'
      ? preCartOffers.find((offer) => offer.orderKey === orderKey)
          ?.newPoseIds ?? []
      : [],
  );
  // A pre-cart URL with nothing new is no offer: browser Back from the cart
  // lands here after every photo was already offered. It returns to the cart,
  // records and logs nothing, and does not hold the parent here.
  const staleOffer = mode === 'precart' && newPoseIds.length === 0;

  // Prevent leaving this page by the back button, except when editing from
  // the cart or visiting from the catalog (whose Cancel goes back). The hook
  // re-pushes the path it is given, so it gets the query string too.
  usePreventNavigation(
    history,
    `${location.pathname}${location.search}`,
    !editMode && !(mode === 'initial' && requestedProductId) && !staleOffer,
  );

  // The pre-cart offer leaves for the cart in place of its own history entry
  // (the cart's gate reached it by replace), so Back from the cart does not
  // return to the offer.
  const replaceWithCart = () => {
    if (history.replace) history.replace('/v3/cart');
    else history.push('/v3/cart');
  };

  const isPrepayOrder = currentOrder?.isPrepay ?? shootIsPrepay ?? false;
  const persistedImageSelectionFlag =
    currentOrder?.additionalData?.retouchingRequiresImageSelection;
  const orderKeyEventId = orderKey?.split('-')[0];
  const belongsToCurrentEvent =
    shootEventId != null &&
    String(currentOrder?.event?.id ?? orderKeyEventId) === String(shootEventId);
  const retouchingRequiresImageSelection = cartOrderMode
    ? belongsToCurrentEvent
      ? globallyEnabledImageSelection === true
      : persistedImageSelectionFlag === true
    : globallyEnabledImageSelection === true;

  // Per-image selection applies only when the company opted in, the order is
  // post-event (images exist), and the cart actually carries images.
  const destinationFlag =
    belongsToCurrentEvent && globallyEnabledImageSelection !== true
      ? false
      : currentOrder?.productContext?.retouchingRequiresImageSelection ??
        retouchingRequiresImageSelection;
  const selectionEnabled: boolean = cartUtils.canSelectRetouchingForOrder({
    order: { ...currentOrder, isPrepay: isPrepayOrder },
    requiresImageSelection: destinationFlag,
  });
  const protectedAttempt = useSelector(
    (state: RetouchingState) =>
      state.v3Order?.retouchingCheckoutProtected ||
      state.v3Order?.retouchingCheckoutRequest ||
      state.v3Order?.retouchingQuoteError,
  );
  // A cart order's retouching opens only when the shared gate lets it be
  // changed, so Add can never end in addToCart's refusal dialog.
  const addableForOrder = useSelector((state: RetouchingState) =>
    selectRetouchingAddableForOrder(state, orderKey),
  );
  const invalidEdit =
    cartOrderMode &&
    (!requestedOrderKey ||
      !currentOrder ||
      !selectionEnabled ||
      !addableForOrder ||
      protectedAttempt);
  // An opted-in order whose photos are all group photos cannot buy
  // retouching at all, so the flat add-on is not offered in their place.
  const groupPhotosOnly =
    destinationFlag === true &&
    !isPrepayOrder &&
    cartUtils.orderHasOnlyGroupPhotos(currentOrder);
  const offersPhotos = selectionEnabled && !invalidEdit;

  // Records the photos shown here as offered, once, on arrival (this also
  // spends the order's one-way post-add prompt), and logs the offer. A cart
  // order the screen refuses shows nothing, so nothing counts as offered.
  useEffect(() => {
    if (invalidEdit || staleOffer) return;
    dispatch(
      recordRetouchingOffered(
        orderKey,
        selectablePoses.map((pose) => pose.id),
      ),
    );
    if (offersPhotos) {
      dispatch(
        logRetouchingOffer(orderKey, {
          surface: source,
          shownAt: new Date().toISOString(),
          photoCount: selectablePoses.length,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // productId -> [pose ids]; prefilled from the cart when editing. A cart
  // saved before levels were exclusive can hold one photo on two levels, and
  // the picker shows only one, so the first-listed level keeps it. A saved id
  // the picker cannot show (a group photo selected before they were excluded)
  // is dropped, as Update drops it: kept out of sight it would still count,
  // price, and demand a note.
  const [selections, setSelections] = useState<RetouchingSelections>(() => {
    if (!cartOrderMode) return {};

    const selectableIds = new Set(selectablePoses.map((pose) => pose.id));
    const saved = (currentOrder?.cartItems || [])
      .filter((item) => item.isRetouching)
      .reduce<RetouchingSelections>(
        (acc, item) => ({
          ...acc,
          [item.productId]: (item.poseIds || []).filter((id) =>
            selectableIds.has(id),
          ),
        }),
        {},
      );
    const prefilled = exclusiveSelections(saved, levelIds);

    // A chip's photo arrives on the chip's level, unless it is already on a
    // line (then it stays where it is).
    const chipLevelId = levels.find(
      (product) => product.id === requestedProductId,
    )?.id;
    if (
      chipPoseId &&
      chipLevelId != null &&
      selectablePoses.some((pose) => pose.id === chipPoseId) &&
      levelForPhoto(prefilled, levelIds, chipPoseId) === null
    ) {
      return setPhotoLevel(prefilled, chipPoseId, chipLevelId);
    }
    return prefilled;
  });

  // The parent's note for each selected photo (pose id -> text), prefilled
  // from the cart when editing, each at most RETOUCHING_NOTE_MAX_LENGTH.
  const [notes, setNotes] = useState<Record<number, string>>(() => {
    if (!cartOrderMode) return {};

    const saved: Record<number, string> = {};
    (currentOrder?.cartItems || [])
      .filter((item) => item.isRetouching)
      .forEach((item) =>
        Object.entries(item.retouchingNotes || {}).forEach(([id, text]) => {
          if (typeof text === 'string') saved[Number(id)] = text;
        }),
      );
    return saved;
  });
  // Photos whose level requires a note but have none, after an Add attempt.
  const [notesMissing, setNotesMissing] = useState<number[]>([]);

  // A chip's photo on a level that requires a note: its note box takes the
  // focus, which is why the chip sent the parent here.
  const focusedChipNote = useRef(false);
  useEffect(() => {
    if (focusedChipNote.current || !chipPoseId || !offersPhotos) return;
    focusedChipNote.current = true;

    const chipLevel = levels.find(
      (product) =>
        product.id === levelForPhoto(selections, levelIds, chipPoseId),
    );
    if (chipLevel?.retouching_note_mode === 'required') {
      document.getElementById(`retouching-note-${chipPoseId}`)?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // productId -> chosen option per option set, prefilled from the level's
  // line when editing. Read through optionsFor, which fills in each set's
  // default, so a required set is never sent empty.
  const [optionChoices, setOptionChoices] = useState<
    Record<number, SelectedProductOptions>
  >(() => {
    const saved: Record<number, SelectedProductOptions> = {};
    (currentOrder?.cartItems || [])
      .filter((item) => item.isRetouching)
      .forEach((item) => {
        saved[item.productId] = (
          item.selectedProductOptions || []
        ).reduce<SelectedProductOptions>(
          (acc, option) =>
            option.optionSetKey
              ? {
                  ...acc,
                  [option.optionSetKey]:
                    option.optionKey || String(option.optionId),
                }
              : acc,
          {},
        );
      });
    return saved;
  });

  useEffect(() => {
    if (invalidEdit) history.push('/v3/cart');
    else if (staleOffer) replaceWithCart();
    // Replace: this screen offers nothing here, so it must not stay on the
    // Back stack under the catalog (Back would bounce straight back).
    else if (groupPhotosOnly) {
      if (history.replace) history.replace('/v3/offers');
      else history.push('/v3/offers');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidEdit, staleOffer, groupPhotosOnly, history]);
  if (invalidEdit)
    return (
      <p role="alert">
        Retouching cannot be edited for this destination. Please return to your
        cart and review the order.
      </p>
    );
  if (staleOffer || groupPhotosOnly) return null;
  if (
    filteredRetouchingProducts.some(
      (product) =>
        !cartUtils.validRetouchingPrice({
          ...product,
          tiered_unit_prices_cents: product.tiered_unit_prices_cents || [],
        }),
    )
  )
    return (
      <p role="alert">
        Retouching pricing needs review. Please return to your cart and refresh
        prices.
      </p>
    );

  // TODO: move this to the middleware
  // If no retouching products are available, push to offerings
  if (filteredRetouchingProducts.length === 0) {
    history.push('/v3/offers');
  }

  const hasOptions = (product: RetouchingProduct) =>
    getProductOptionSets(product).length > 0;
  const optionsFor = (product: RetouchingProduct): SelectedProductOptions =>
    reconcileProductOptions(product, optionChoices[product.id] || {});
  // A product with option sets sends them by set; one with a legacy flat
  // list sends its single choice as productOptionKey.
  const optionArgsFor = (product: RetouchingProduct) =>
    productOptionArgs(product, optionChoices[product.id] || {});
  const optionFieldsFor = (
    product: RetouchingProduct,
  ): { selectedProductOptions?: SavedProductOption[] } => {
    const { productOptions = {}, productOptionKey = null } =
      optionArgsFor(product);
    return cartUtils.productOptionCartFields(
      product,
      productOptions,
      productOptionKey,
    );
  };
  const optionFields = (product: RetouchingProduct) =>
    hasOptions(product) ? (
      <ProductOptionFields
        product={product}
        idPrefix={`retouching-${product.id}-`}
        selectedOptions={optionsFor(product)}
        onChange={(optionSetKey, optionValue) =>
          setOptionChoices((current) => ({
            ...current,
            [product.id]: {
              ...reconcileProductOptions(product, current[product.id] || {}),
              [optionSetKey]: optionValue,
            },
          }))
        }
      />
    ) : null;

  const addProductToCart = (product: RetouchingProduct) => {
    dispatch(
      addRetouchingToCart({
        studentId: selectedStudent.id,
        fullproductData: product,
        ...optionArgsFor(product),
      }),
    );
  };

  const anySelections = levels.some(
    (product) => (selections[product.id] || []).length > 0,
  );
  const existingRetouchingItems = (currentOrder?.cartItems || []).filter(
    (item) => item.isRetouching,
  );
  const hasLineForLevels = existingRetouchingItems.some((item) =>
    levels.some((product) => product.id === item.productId),
  );
  // On a cart order an empty selection still submits: it clears the line.
  const canSubmit = anySelections || (cartOrderMode && hasLineForLevels);

  // The source each photo already has, from whichever line holds it, and the
  // photos already on a line. First touch wins, so a photo moving to another
  // level keeps its entry, and only a photo new to retouching takes this
  // screen's source.
  const existingSources: Record<number, string> = {};
  const alreadyRetouched = new Set<number>();
  existingRetouchingItems.forEach((item) => {
    (item.poseIds || []).forEach((id) => alreadyRetouched.add(Number(id)));
    Object.entries(item.retouchingSources || {}).forEach(([id, value]) => {
      if (value && existingSources[Number(id)] == null)
        existingSources[Number(id)] = value;
    });
  });
  const sourcesFor = (poseIds: number[]) =>
    poseIds.reduce<Record<number, RetouchingSource>>((acc, poseId) => {
      const value = existingSources[poseId] as RetouchingSource | undefined;
      if (value) return { ...acc, [poseId]: value };
      return alreadyRetouched.has(poseId) ? acc : { ...acc, [poseId]: source };
    }, {});

  // The notes a line carries: its selected photos' non-blank notes, and none
  // at all for a level that does not take notes.
  const notesFor = (product: RetouchingProduct, poseIds: number[]) => {
    const mode = product.retouching_note_mode;
    if (mode !== 'optional' && mode !== 'required') return {};

    return poseIds.reduce<Record<number, string>>((acc, poseId) => {
      const text = (notes[poseId] || '').trim();
      return text ? { ...acc, [poseId]: text } : acc;
    }, {});
  };

  const addSelectedToCart = () => {
    if (!canSubmit) return;

    const missing = levels
      .filter((product) => product.retouching_note_mode === 'required')
      .flatMap((product) => selections[product.id] || [])
      .filter((poseId) => !(notes[poseId] || '').trim());
    setNotesMissing(missing);
    if (missing.length > 0) {
      document.getElementById(`retouching-note-${missing[0]}`)?.focus();
      return;
    }

    if (cartOrderMode) {
      // Lines the customer cleared entirely. Incentives remain in place until
      // finishRetouchingEdit can reconcile order-level and cart-level rules.
      // A legacy line with no saved photos opens with nothing selected, so an
      // empty selection there is no request to remove it.
      existingRetouchingItems
        .filter(
          (item) =>
            (item.poseIds || []).length > 0 &&
            levels.some((product) => product.id === item.productId) &&
            (selections[item.productId] || []).length === 0,
        )
        .forEach((item) =>
          dispatch(clearRetouchingSelection(item.id, orderKey)),
        );
    }

    levels.forEach((product) => {
      const selectedIds = selections[product.id] || [];
      if (selectedIds.length === 0) return;

      const selectedPoses = selectedIds
        .map((id) => selectablePoses.find((pose) => pose.id === id))
        .filter((pose): pose is RetouchingPose => Boolean(pose));

      const existingItem = existingRetouchingItems.find(
        (item) => item.productId === product.id,
      );
      const productNotes = notesFor(product, selectedIds);
      const productSources = sourcesFor(selectedIds);

      // Editing a line that already exists is an in-place, order-scoped update.
      // addToCart must NOT be used here: it derives its order key from the
      // GLOBALLY selected student, not from the order this page was opened
      // against, so on a multi-student cart the old remove-then-re-add would
      // delete this order's retouching and re-add it to whichever student
      // happened to be selected -- or hit addToCart's "already in your cart"
      // guard and re-add nothing at all, silently dropping the line.
      if (existingItem) {
        dispatch(
          updateRetouchingSelection(
            orderKey,
            existingItem.id,
            selectedPoses,
            product.price,
            productNotes,
            {
              sources: productSources,
              // A level without options leaves the line's own options alone.
              ...(hasOptions(product)
                ? { optionFields: optionFieldsFor(product) }
                : {}),
            },
          ),
        );
        return;
      }

      dispatch(
        addRetouchingToCart({
          studentId: selectedStudent.id,
          // A retouching product the edited order does not have yet still has
          // to land on THAT order. Without the override addToCart derives its
          // destination from the globally selected student, so editing student
          // B while A is selected would add B's line to A's order.
          orderKeyOverride: cartOrderMode ? orderKey : null,
          fullproductData: product,
          retouchingPricingMode: 'per_image',
          poseIds: selectedPoses.map((pose) => pose.id),
          poses: selectedPoses,
          quantity: selectedPoses.length,
          // A cart-order add happens in place (no navigation): finishRetouchingEdit
          // below returns to the cart (pre-cart replaces the offer's entry), so a
          // 'cart' redirect here would push a second cart entry onto the Back
          // stack, or leave the stale offer URL under it.
          redirectSource: cartOrderMode ? 'stay' : null,
          retouchingNotes: productNotes,
          retouchingSources: productSources,
          ...optionArgsFor(product),
        }),
      );
    });

    dispatch(resolveRetouchingOffer(orderKey, 'added'));
    if (mode === 'precart') {
      // Same Back-stack shape as No Thanks: the cart replaces the offer.
      replaceWithCart();
      dispatch(finishRetouchingEdit(orderKey, { stayOnCart: true }));
    } else if (cartOrderMode) {
      dispatch(finishRetouchingEdit(orderKey));
    }
  };

  const multipleLevels = levels.length > 1;
  const summaries = levels
    .filter((product) => (selections[product.id] || []).length > 0)
    .map((product) => {
      const { text, totalCents } = selectionSummary(
        product,
        (selections[product.id] || []).length,
        optionFieldsFor(product).selectedProductOptions || [],
      );
      return {
        id: product.id,
        text: multipleLevels ? `${product.name}: ${text}` : text,
        totalCents,
      };
    });

  const selectedPhotoCount = summaries.reduce(
    (count, summary) => count + (selections[summary.id] || []).length,
    0,
  );

  const cancelButton = (
    <ActionButton
      type="button"
      className="bnl-product-config__button--cancel button bg--dark-grey"
      onClick={() => {
        if (offersPhotos)
          dispatch(resolveRetouchingOffer(orderKey, 'declined'));
        // A catalog visit goes back to the catalog, also when the order
        // already has retouching (edit mode); a chip visit returns to the cart.
        if (requestedProductId && !chipPoseId) {
          if (history.goBack) history.goBack();
          else history.push('/v3/offers');
        } else if (mode === 'precart') replaceWithCart();
        else if (cartOrderMode) history.push('/v3/cart');
        else dispatch(declineRetouching(orderKey));
      }}
    >
      {editMode || (mode === 'initial' && requestedProductId)
        ? 'Cancel'
        : 'No Thanks'}
    </ActionButton>
  );

  if (!selectionEnabled) {
    return (
      <>
        <Title>Retouching</Title>
        <FlatIntro>
          Select &apos;Add Retouching&apos; if you would like to add it to your
          order.
        </FlatIntro>
        <Levels>
          {filteredRetouchingProducts.map((product) => (
            <RetouchingLevelCard
              key={product.id}
              product={product}
              perImagePricing={false}
              options={optionFields(product)}
              action={
                <ActionButton
                  type="button"
                  className="bnl-product-config__button button"
                  onClick={() => addProductToCart(product)}
                >
                  Add {product.name}
                </ActionButton>
              }
            />
          ))}
        </Levels>
        <Actions>{cancelButton}</Actions>
      </>
    );
  }

  // With several levels the intro names the one a tap selects.
  const intro =
    mode === 'precart'
      ? 'You added new photos. Would you like them retouched?'
      : multipleLevels && defaultLevel
      ? `Tap the photos you would like ${defaultLevel.name} on, or choose a level for each photo.`
      : 'Tap the photos you would like retouched.';

  return (
    <>
      <Title>Retouching</Title>
      <Intro>{intro}</Intro>
      <Caption>{RETOUCHING_ORIGINALS_CAPTION}</Caption>
      <Levels>
        {levels.map((product) => (
          <RetouchingLevelCard
            key={product.id}
            product={product}
            perImagePricing
            // One level on its own lies across the page as the page's
            // subject; several stack as a row to compare.
            variant={multipleLevels ? 'card' : 'summary'}
            options={optionFields(product)}
          />
        ))}
      </Levels>
      <RetouchingPhotoPicker
        orderKey={orderKey}
        poses={selectablePoses}
        levels={levels}
        selections={selections}
        newPoseIds={newPoseIds}
        onTogglePhoto={(poseId) =>
          setSelections((current) =>
            togglePhoto(current, poseId, (defaultLevel ?? levels[0]).id),
          )
        }
        onChooseLevel={(poseId, productId) =>
          setSelections((current) => setPhotoLevel(current, poseId, productId))
        }
        notes={notes}
        notesMissing={notesMissing}
        onNoteChange={(poseId, text) => {
          setNotes((current) => ({ ...current, [poseId]: text }));
          if (text.trim())
            setNotesMissing((current) => current.filter((id) => id !== poseId));
        }}
      />
      <ActionBar>
        <Summary aria-live="polite">
          {summaries.length === 0 && 'No photos selected yet.'}
          {summaries.map((summary) => (
            <SummaryLine
              key={summary.id}
              $collapseOnPhone={summaries.length > 1}
            >
              {summary.text}
            </SummaryLine>
          ))}
          {summaries.length > 1 && (
            <PhoneCount aria-hidden="true">
              {selectedPhotoCount} photos selected
            </PhoneCount>
          )}
          {summaries.length > 1 && (
            <SummaryTotal>
              Total{' '}
              {formatUSD(
                summaries.reduce((sum, summary) => sum + summary.totalCents, 0),
              )}
            </SummaryTotal>
          )}
          {notesMissing.length > 0 && (
            <NotesMissing role="alert">
              Add a note for{' '}
              {notesMissing
                .map(
                  (poseId) =>
                    `Photo ${
                      selectablePoses.findIndex((pose) => pose.id === poseId) +
                      1
                    }`,
                )
                .join(', ')}
              .
            </NotesMissing>
          )}
        </Summary>
        <Actions>
          {cancelButton}
          <ActionButton
            type="button"
            disabled={!canSubmit}
            className={`bnl-product-config__button button${
              canSubmit ? '' : ' bnl-product-config__button--disabled'
            }`}
            onClick={addSelectedToCart}
            style={
              canSubmit ? undefined : { opacity: 0.5, cursor: 'not-allowed' }
            }
          >
            {cartOrderMode && hasLineForLevels
              ? 'Update Retouching'
              : 'Add Retouching'}
          </ActionButton>
        </Actions>
      </ActionBar>
    </>
  );
};

export default RetouchingConfig;
